/**
 * ConfidenceRing
 *
 * Circular SVG gauge that renders a score from 0-100 as a proportional
 * arc stroke.  The arc color matches the confidence band.  An optional
 * label renders the numeric score in the center using a monospace font.
 */
import React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { semanticColors } from '../../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Band color (same thresholds as ConfidenceBadge)                    */
/* ------------------------------------------------------------------ */

// #0078D4 (classic Fluent "communication blue"), #F59E0B (amber), and #EF4444
// (red) are not in brand-config.json's palette — no token represents them, so
// they are left as raw literals to avoid changing these band colours.
function bandColor(score: number): string {
  if (score >= 90) return semanticColors.success;
  if (score >= 75) return '#0078D4';
  if (score >= 55) return '#F59E0B';
  if (score >= 35) return semanticColors.warning;
  return '#EF4444';
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  wrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  label: {
    position: 'absolute',
    fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    userSelect: 'none',
  },
});

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ConfidenceRingProps {
  /** Confidence score 0-100. */
  score: number;
  /** Diameter in pixels. @default 64 */
  size?: number;
  /** Show the numeric score in the center. @default true */
  showLabel?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ConfidenceRing: React.FC<ConfidenceRingProps> = ({
  score,
  size = 64,
  showLabel = true,
}) => {
  const styles = useStyles();
  const clamped = Math.max(0, Math.min(100, Math.round(score)));

  const strokeWidth = Math.max(3, size * 0.1);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clamped / 100) * circumference;
  const color = bandColor(clamped);
  const center = size / 2;

  // Font size scales with ring diameter
  const fontSize = Math.max(10, size * 0.28);

  return (
    <div
      className={styles.wrapper}
      style={{ width: size, height: size }}
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Confidence ${clamped}%`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={tokens.colorNeutralStroke2}
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${center} ${center})`}
          style={{ transition: 'stroke-dashoffset 0.4s ease' }}
        />
      </svg>

      {showLabel && (
        <span className={styles.label} style={{ fontSize }}>
          {clamped}
        </span>
      )}
    </div>
  );
};
